/**
 * 出庫標籤／契約值 → 顯示（**單一位置**）
 *
 * ⚠️ 呢啲值係**後端契約**（`@coeldery/outbound-order` 嘅 `OUTBOUND_KINDS`／`OUTBOUND_STATUSES`／
 *    `DELIVERY_MODES`）—— 前端**唔准**自己發明新值；加值要同時改模組＋migration＋呢度。
 * ⚠️ 「去向」名**唔喺呢度寫死**：去向係 DB（`destinations` 表）嘅資料，呢度只係揀語言顯示。
 */
import { useTranslation } from 'react-i18next';
import { badge } from '@coeldery/ui-kit';
import type { OutboundKind, OutboundStatus, DeliveryMode } from '../lib/api';

const TONE: Record<OutboundStatus, 'ok' | 'off' | 'warn' | 'info' | 'sponsored'> = {
  draft: 'info',
  sent: 'sponsored',
  acknowledged: 'info',
  delivered: 'ok',
  reconciled: 'ok',
  closed: 'off',
  cancelled: 'off',
};

const LABEL: Record<OutboundStatus, string> = {
  draft: 'out.statusDraft',
  sent: 'out.statusSent',
  acknowledged: 'out.statusAcknowledged',
  delivered: 'out.statusDelivered',
  reconciled: 'out.statusReconciled',
  closed: 'out.statusClosed',
  cancelled: 'out.statusCancelled',
};

/** 狀態 → i18n key（**單一來源**：標籤／篩選器共用） */
export const statusLabelKey = (status: OutboundStatus): string => LABEL[status] ?? 'out.statusDraft';

/** 篩選器用（`reconciled` ＝ 場後結算完成，未到完單） */
export const OUTBOUND_STATUSES: OutboundStatus[] = [
  'draft',
  'sent',
  'acknowledged',
  'delivered',
  'reconciled',
  'closed',
  'cancelled',
];

export function OutboundStatusChip({ status }: { status: OutboundStatus }) {
  const { t } = useTranslation();
  return <span style={badge(TONE[status] ?? 'info')}>{t(statusLabelKey(status))}</span>;
}

// ── 類型／取貨方式（純標籤，唔用 chip 色：避免同狀態色混淆）─────────────
const KIND_LABEL: Record<OutboundKind, string> = {
  direct_sale: 'out.kindDirectSale',
  delivery: 'out.kindDelivery',
};

export const kindLabelKey = (kind: OutboundKind | string): string =>
  KIND_LABEL[kind as OutboundKind] ?? 'out.kindDirectSale';

export const OUTBOUND_KINDS: OutboundKind[] = ['direct_sale', 'delivery'];

const MODE_LABEL: Record<DeliveryMode, string> = {
  self_pickup: 'out.modeSelfPickup',
  logistics: 'out.modeLogistics',
};

export const modeLabelKey = (mode: DeliveryMode | string): string =>
  MODE_LABEL[mode as DeliveryMode] ?? 'out.modeSelfPickup';

export const DELIVERY_MODES: DeliveryMode[] = ['self_pickup', 'logistics'];

/**
 * 雙語標籤（DB 出嘅受控清單：去向／剩貨去向／店）—— 由前端按當前語言揀。
 * ⚠️ 同 `Destination` 一樣兩個語言都帶（server 唔知用戶而家係咩語言）。
 */
export interface BilingualLabel {
  labelZhHk: string;
  labelEn: string;
}

/**
 * 標籤名：DB 一律**兩個語言都帶**（`labelZhHk`／`labelEn`）→ 由前端按當前語言揀。
 * （唔由 server 揀：server 唔知用戶而家係咩語言。）
 */
export function optionLabel(d: BilingualLabel, lang: string): string {
  return lang.toLowerCase().startsWith('en') ? d.labelEn : d.labelZhHk;
}

/** 「去向」名（同 `optionLabel()` 同一招；保留舊名唔使改既有呼叫） */
export const destinationLabel = optionLabel;

